import * as types from './actionTypes';

import { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import {
    Ifacility,
    IfacilityWithoutBuildings,
    Ioption,
    ThunkResult
} from '../models';

import { isEmpty } from 'lodash';
import { toastr } from 'react-redux-toastr';
import { msalFetch } from '../components/auth/Auth-Utils';
import API from '../constants/apiEndpoints';
import { constants } from '../constants/constants';
import { convertFacillitiesToOptionsWithCountryID } from '../reducers/facilitiesReducer';
import { beginAjaxCall, endAjaxCall } from './ajaxStatusActions';

export interface FacilitiesSearchParams {
    name?: string;
    address?: string;
    postalCode?: string;
}

const uuidv4 = require('uuid/v4');

export const closeAllModals = () => ({
    type: types.CLOSE_ALL_MODALS
});
export const toggleEditFacilityModal = () => ({
    type: types.TOGGLE_MODAL_EDIT_FACILITY
});
export const toggleSideMenu = () => ({
    type: types.TOGGLE_MODAL_SIDE_MENU
});

export function getFacilitiesByCountry(
    filterParams: FacilitiesSearchParams
): ThunkResult<any> {
    const cleanParams = (obj: FacilitiesSearchParams): FacilitiesSearchParams =>
        Object.fromEntries(
            Object.entries(obj).filter(
                ([_, val]) => val !== null && val !== undefined
            )
        );
    const params = isEmpty(filterParams)
        ? undefined
        : cleanParams(filterParams);

    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params
        };

        const url = API.GET.facility.search;
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>): { hasFacilities: boolean } => {
                dispatch(endAjaxCall());
                if (!data.data) {
                    throw new Error('missing data');
                } else {
                    dispatch({
                        type: types.GET_FACILITIES_SUCCESS,
                        facilities: data.data
                    });
                    if (data.data.length === 0) {
                        return { hasFacilities: false };
                    } else {
                        return { hasFacilities: true };
                    }
                }
            })
            .catch((error: any) => {
                dispatch(endAjaxCall());
                dispatch({
                    type: types.GET_FACILITIES_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'get facilities search');
                console.error(error);
            });
    };
}

export function searchFacilityQueueForAsyncSelect(
    name: string | undefined,
    countryId: string | undefined,
    callback: any,
    saveFacilities: any,
    standardIDs?: string[]
): ThunkResult<any> {
    return (dispatch, getState) => {
        let url: string;
        let params: any = { name };

        // API is expecting a Guid, don't send an empty string
        if (countryId) {
            params = { ...params, countryId };
        }

        // Multi-select Standard filter (MMG-4352). Sent as one comma-separated
        // string: axios serializes an array as `standards[]=`, which the API's
        // query-string binder does not read.
        if (standardIDs && standardIDs.length) {
            params = { ...params, standards: standardIDs.join(',') };
        }

        let data: any;
        let method: Method = 'get';

        url = API.GET.facility.GetFacilityQueue;

        const axiosOptions: AxiosRequestConfig = {
            method,
            params,
            data
        };

        if (!name && name !== undefined) {
            return;
        }

        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    throw new Error('missing data');
                }
                saveFacilities(data.data);
                callback(convertFacillitiesToOptionsWithCountryID(data.data));
            })
            .catch((error: any) => {
                constants.handleError(error, 'get facilities search');
                console.error(error);
            });
    };
}

export function searchFacilityByNameAsyncSelect(
    name: string,
    callback: (facilities: Ioption[]) => void,
    saveFacilities: (facilities: Ifacility[]) => void
): ThunkResult<any> {
    return (dispatch, getState) => {
        if (!name && name !== undefined) {
            console.warn('searchFacilityByNameAsyncSelect: no name');
            return;
        }

        return msalFetch(API.GET.facility.search, {
            method: 'get',
            params: { name }
        })
            .then((response: AxiosResponse<any>) => {
                if (!response.data) {
                    throw new Error('missing data');
                }

                saveFacilities(response.data);
                callback(
                    convertFacillitiesToOptionsWithCountryID(response.data)
                );
            })
            .catch((error: any) => {
                dispatch(endAjaxCall());
                constants.handleError(error, 'get facilities search by name');
                console.error(error);
            });
    };
}

export function searchFacilitiesForAsyncSelect(
    name: string | undefined,
    countryId: string | null | string[],
    quantityOfAssets: number | null,
    callback: any,
    saveFacilities: any,
    page?: number,
    callbackWithPagination?: any,
    standardIDs?: string[]
): ThunkResult<any> {
    return (dispatch, getState) => {
        let url: string;
        const params: any = { search: name, quantityOfAssets, page: page || 1 };

        // Multi-select Standard filter (MMG-4352). Sent as one comma-separated
        // string: axios serializes an array as `standards[]=`, which the API's
        // query-string binder does not read.
        if (standardIDs && standardIDs.length) {
            params.standards = standardIDs.join(',');
        }
        let data: any;
        let method: Method = 'get';

        if (countryId !== null && countryId !== '' && countryId !== undefined) {
            data = Array.isArray(countryId) ? countryId : [countryId];
            url = API.GET.facility.searchByCountries;
            method = 'put';
        } else {
            url = API.GET.facility.search;
        }

        const axiosOptions: AxiosRequestConfig = {
            method,
            params,
            data
        };

        if (!name && name !== undefined) {
            return;
        }

        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                dispatch(endAjaxCall());
                if (!data.data) {
                    throw new Error('missing data');
                }
                saveFacilities(data.data.result);
                callback(
                    convertFacillitiesToOptionsWithCountryID(data.data.result)
                );
                if (
                    callbackWithPagination &&
                    typeof callbackWithPagination === 'function'
                )
                    callbackWithPagination(data.data);
            })
            .catch((error: any) => {
                dispatch(endAjaxCall());
                constants.handleError(error, 'get facilities search');
                console.error(error);
            });
    };
}

export function getFacility(facilityID: string): Promise<void | Ifacility> {
    const axiosOptions: AxiosRequestConfig = {
        method: 'get'
    };

    const url = `${API.GET.facility.getbyid}/${facilityID}`;
    return msalFetch(url, axiosOptions)
        .then((data: AxiosResponse<any>) => {
            if (!data.data) {
                throw new Error('missing data');
            } else {
                return data.data as Ifacility;
            }
        })
        .catch((error: any) => {
            constants.handleError(error, 'get facility locations');
            console.error(error);
        });
}

export function addFacility(
    facility: IfacilityWithoutBuildings
): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        const newID = uuidv4();
        const contractWithNewFacilityID = facility.contract
            ? { ...facility.contract, facilityID: newID }
            : null;
        const data = {
            ...facility,
            id: newID,
            contract: contractWithNewFacilityID
        };
        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            data
        };

        const url = API.POST.facility.add;
        return msalFetch(url, axiosOptions)
            .then((response: AxiosResponse<any>) => {
                if (!response.data) {
                    throw new Error('missing data');
                } else {
                    dispatch({
                        type: types.FACILITY_UPDATE_SUCCESS,
                        facility: response.data
                    });
                    dispatch({ type: types.TOGGLE_MODAL_EDIT_FACILITY });
                    toastr.success(
                        'Success',
                        'Saved Facility',
                        constants.toastrSuccess
                    );
                    // wait for the select options to update then trigger an event that a new facility has been added.
                    setTimeout(() => {
                        const event = new CustomEvent('newFacility', {
                            detail: response.data.id
                        });
                        document.dispatchEvent(event);
                        document.dispatchEvent(
                            new CustomEvent('updatedCustomers')
                        );
                    }, 400);

                    return response;
                }
            })
            .catch((error: any) => {
                dispatch({
                    type: types.FACILITY_UPDATE_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'add facility');
                console.error(error);
            });
    };
}

export function updateFacility(
    facility: IfacilityWithoutBuildings
): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());

        // Function to convert empty strings to undefined
        const convertEmptyStringsToUndefined = (obj: {
            [key: string]: any;
        }) => {
            for (const key in obj) {
                if (obj[key] === '') {
                    obj[key] = undefined;
                } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                    convertEmptyStringsToUndefined(obj[key]);
                }
            }
        };

        // Convert empty strings to undefined in the facility object
        convertEmptyStringsToUndefined(facility);

        const axiosOptions: AxiosRequestConfig = {
            method: 'put',
            data: facility
        };

        const url = API.PUT.updateFacility.replace('{id}', facility.id);
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                dispatch({
                    type: types.FACILITY_UPDATE_SUCCESS,
                    facility
                });
                dispatch({ type: types.TOGGLE_MODAL_EDIT_FACILITY });
                toastr.success(
                    'Success',
                    'Updated Facility',
                    constants.toastrSuccess
                );
                document.dispatchEvent(new CustomEvent('updatedCustomers'));
            })
            .catch((error: any) => {
                dispatch({
                    type: types.FACILITY_UPDATE_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'update facility');
                console.error(error);
            });
    };
}

export function deleteFacility(): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        const axiosOptions: AxiosRequestConfig = {
            method: 'delete'
        };
        const { selectedFacilityID } = getState().manageFacility;
        const url = API.DELETE.facility.replace('{id}', selectedFacilityID);
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                dispatch({
                    type: types.FACILITY_DELETE_SUCCESS,
                    payload: selectedFacilityID
                });
                dispatch({ type: types.TOGGLE_MODAL_EDIT_FACILITY });
                toastr.success(
                    'Success',
                    'Deleted Facility',
                    constants.toastrSuccess
                );
                document.dispatchEvent(new CustomEvent('updatedCustomers'));
            })
            .catch((error: any) => {
                dispatch({
                    type: types.FACILITY_DELETE_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'delete facility');
                console.error(error);
            });
    };
}
// TODO rename to global set
export const setSelectedFacilityID = (id: string) => ({
    type: types.GLOBAL_SET_SELECTED_FACILITY_ID,
    id
});

export const clearSelectedFacilityID = () => ({
    type: types.GLOBAL_CLEAR_SELECTED_FACILITY_ID
});

export function getSAPJobMapping(): ThunkResult<any> {
    return (dispatch, getState) => {
        // If we already have the data, don't make the call again, this data should pretty much never change
        if (
            Object.keys(getState().sapJobMappings.sapJobMappingsByID).length > 0
        ) {
            return Promise.resolve();
        }

        dispatch(beginAjaxCall());
        const axiosOptions: AxiosRequestConfig = {
            method: 'get'
        };

        const url = API.GET.sapJobMapping.getAll;
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    throw new Error('missing data');
                } else {
                    dispatch({
                        type: types.SET_SAP_JOB_MAPPING,
                        data: data.data
                    });
                }
            })
            .catch((error: any) => {
                constants.handleError(error, 'get sap job mapping');
                console.error('[getSAPJobMapping]:', error);
            })
            .finally(() => {
                dispatch(endAjaxCall());
            });
    };
}

// Add these new actions for multi-select facilities
export const toggleSelectedFacility = (facilityId: string) => ({
    type: types.TOGGLE_SELECTED_FACILITY,
    facilityId
});

export const setSelectedFacilities = (facilityIds: string[]) => ({
    type: types.SET_SELECTED_FACILITIES,
    facilityIds
});

export const clearSelectedFacilities = () => ({
    type: types.CLEAR_SELECTED_FACILITIES
});
